import { useCallback, useState } from 'react';

import { logger } from '@/utils/logger';

/**
 * Custom hook for copying text to clipboard with visual feedback
 *
 * @param resetDelay - Time in milliseconds before resetting the copied state (default: 4000ms)
 * @returns Object with copied state and copy function
 *
 * @example
 * const { copied, copy } = useCopyToClipboard();
 * <Button onClick={() => copy(secretValue)}>{copied ? 'Copied!' : 'Copy'}</Button>
 */
export const useCopyToClipboard = (resetDelay = 4000) => {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(
    (text: string) => {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), resetDelay);
        })
        .catch((err) => {
          logger.error('Failed to copy text:', err);
        });
    },
    [resetDelay]
  );

  return { copied, copy };
};
